import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !error.config?.url?.includes('/auth/login')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    // Surface read-only 403 as a toast once per request. Callers can still
    // catch it if they want a custom message.
    if (error.response?.status === 403 && /read-only/i.test(error.response?.data?.detail || '')) {
      try {
        // Lazy-import to avoid a hard dep at module load time.
        import('sonner').then(({ toast }) => toast.error(error.response.data.detail));
      } catch { /* ignore */ }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
};

// Restaurants
export const restaurantsAPI = {
  getAll: () => api.get('/restaurants'),
  placementsByCycle: (id) => api.get(`/restaurants/${id}/placements-by-cycle`),
  duplicates: () => api.get('/restaurants/duplicates'),
  merge: (keepId, mergeFromId) => api.post(`/restaurants/${keepId}/merge`, { merge_from_id: mergeFromId }),
  create: (data) => api.post('/restaurants', data),
  update: (id, data) => api.put(`/restaurants/${id}`, data),
  delete: (id) => api.delete(`/restaurants/${id}`),
  downloadTemplate: () => api.get('/restaurants/template', { responseType: 'blob' }),
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/restaurants/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Students
export const studentsAPI = {
  getAll: () => api.get('/students'),
  enrollmentYears: () => api.get('/students/enrollment-years'),
  getOne: (id) => api.get(`/students/${id}`),
  getPlacements: (id) => api.get(`/students/${id}/placements`),
  create: (data) => api.post('/students', data),
  update: (id, data) => api.put(`/students/${id}`, data),
  delete: (id) => api.delete(`/students/${id}`),
  bulkDelete: (ids) => api.post('/students/bulk-delete', { ids }),
  promotePreview: (body) => api.post('/students/promote/preview', body),
  promote: (body) => api.post('/students/promote', body),
  archive: (id) => api.post(`/students/${id}/archive`),
  unarchive: (id) => api.post(`/students/${id}/unarchive`),
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/students/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getPerformanceReport: (id) => api.get(`/students/${id}/performance-report`),
  downloadTemplate: () => api.get('/students/template', { responseType: 'blob' }),
  exportExcel: (ids) => (ids ? api.post('/students/export', { ids }, { responseType: 'blob' }) : api.get('/students/export', { responseType: 'blob' })),
};

// Placements
export const placementsAPI = {
  getAll: (status) => api.get('/placements', { params: { status } }),
  getDetails: (placementId) => api.get(`/placements/${placementId}/details`),
  create: (data) => api.post('/placements', data),
  update: (id, data) => api.put(`/placements/${id}`, data),
  delete: (id) => api.delete(`/placements/${id}`),
  archiveFeedback: (id) => api.post(`/placements/${id}/archive-feedback`),
  unarchiveFeedback: (id) => api.post(`/placements/${id}/unarchive-feedback`),
  sendAck: (id) => api.post(`/placements/${id}/send-ack`),
  exportExcel: (ids) => (ids ? api.post('/placements/export', { ids }, { responseType: 'blob' }) : api.get('/placements/export', { responseType: 'blob' })),
};

// Dashboard
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

// Users (Admin only)
export const usersAPI = {
  getAll: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// Settings (Admin only)
export const settingsAPI = {
  get: () => api.get('/settings'),
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/settings/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteLogo: () => api.delete('/settings/logo'),
  resetDatabase: (data) => api.post('/settings/reset-database', data),
};

// Feedback Templates
export const feedbackTemplatesAPI = {
  getAll: () => api.get('/feedback-templates'),
  getOne: (id) => api.get(`/feedback-templates/${id}`),
  create: (data) => api.post('/feedback-templates', data),
  update: (id, data) => api.put(`/feedback-templates/${id}`, data),
  delete: (id) => api.delete(`/feedback-templates/${id}`),
};

// Feedback Forms
export const feedbackFormsAPI = {
  getAll: (params) => api.get('/feedback-forms', { params }),
  create: (data) => api.post('/feedback-forms', data),
  createBulk: (data) => api.post('/feedback-forms/bulk', data),
  patch: (id, data) => api.patch(`/feedback-forms/${id}`, data),
  delete: (id) => api.delete(`/feedback-forms/${id}`),
  getSubmission: (formId) => api.get(`/feedback-submissions/${formId}`),
  getStudentSubmissions: (studentId) => api.get(`/feedback-submissions/student/${studentId}`),
};

// Public Feedback (no auth)
export const publicFeedbackAPI = {
  getByToken: (token) => axios.get(`${API_URL}/api/feedback/access/${token}`),
  verifyCode: (code, studentNumber) => axios.post(`${API_URL}/api/feedback/verify-code`, null, { params: { code, student_number: studentNumber } }),
  submit: (token, data) => axios.post(`${API_URL}/api/feedback/submit/${token}`, data),
};

// Public Settings (no auth - for logo on public forms)
export const publicSettingsAPI = {
  get: () => axios.get(`${API_URL}/api/settings/public`),
};

// Placement Settings
export const placementSettingsAPI = {
  get: () => api.get('/placement-settings'),
  update: (data) => api.put('/placement-settings', data),
};

// Course Types catalogue
export const courseTypesAPI = {
  list: () => api.get('/course-types'),
  detail: () => api.get('/course-types/detail'),
  setLength: (name, course_length) => api.put(`/course-types/${encodeURIComponent(name)}/length`, { course_length }),
  add: (name) => api.post('/course-types', { name }),
  remove: (name) => api.delete(`/course-types/${encodeURIComponent(name)}`),
};

// Tasks
export const tasksAPI = {
  list: (params) => api.get('/tasks', { params }),
  buckets: () => api.get('/tasks/buckets'),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  complete: (id) => api.post(`/tasks/${id}/complete`),
  delete: (id) => api.delete(`/tasks/${id}`),
};

// Contract Templates (admin)
export const contractTemplatesAPI = {
  list: (params) => api.get('/contract-templates', { params }),
  get: (id) => api.get(`/contract-templates/${id}`),
  create: (data) => api.post('/contract-templates', data),
  update: (id, data) => api.put(`/contract-templates/${id}`, data),
  delete: (id) => api.delete(`/contract-templates/${id}`),
  seedDefaults: (force = false) => api.post(`/contract-templates/seed-defaults${force ? '?force=true' : ''}`),
  clone: (id) => api.post(`/contract-templates/${id}/clone`),
  importDocx: (file, { name, category, year_of_study }) => {
    const fd = new FormData();
    fd.append('file', file);
    const qs = new URLSearchParams({ name: name || '', category, year_of_study }).toString();
    return api.post(`/contract-templates/import-docx?${qs}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Industry Placement Letter (one per restaurant per cycle)
export const industryLettersAPI = {
  create: (data) => api.post('/contracts/industry-letter', data),
  preview: (data) => api.post('/contracts/bulk/industry-letters/preview', data),
  generate: (data) => api.post('/contracts/bulk/industry-letters/generate', data),
  send: (data) => api.post('/contracts/bulk/industry-letters/send', data),
};

// Student enums (used by cohort filters)
export const studentEnumsAPI = {
  list: () => api.get('/students/enums'),
};

// Bulk Student Placement Letter wizard
export const placementLettersBulkAPI = {
  preview: (data) => api.post('/contracts/bulk/placement-letters/preview', data),
  generate: (data) => api.post('/contracts/bulk/placement-letters/generate', data),
  send: (data) => api.post('/contracts/bulk/placement-letters/send', data),
};

// Contract Instances
export const contractsAPI = {
  listForRestaurant: (restaurantId) => api.get(`/restaurants/${restaurantId}/contracts`),
  listForPlacement: (placementId) => api.get(`/placements/${placementId}/contracts`),
  get: (id) => api.get(`/contracts/${id}`),
  create: (data) => api.post('/contracts', data),
  send: (id, payload) => api.post(`/contracts/${id}/send`, payload),
  void: (id) => api.post(`/contracts/${id}/void`),
  delete: (id) => api.delete(`/contracts/${id}`),
  refresh: (id) => api.post(`/contracts/${id}/refresh`),
  reissue: (id, reason) => api.post(`/contracts/${id}/reissue`, { reason }),
  bulkRefresh: (cycleId, categories) => api.post('/contracts/bulk/refresh', { cycle_id: cycleId, categories }),
  repairMovedPlacements: (cycleId) => api.post('/contracts/repair-moved-placements', { cycle_id: cycleId }),
  // Public signing
  getPublic: (token) => api.get(`/contracts/sign/${token}`),
  signPublic: (token, data) => api.post(`/contracts/sign/${token}`, data),
};

// ICA signature settings
export const icaSignatureAPI = {
  update: (data) => api.put('/settings/ica-signature', data),
};

// Email
export const emailAPI = {
  sendAssessment: (data) => api.post('/email/send-assessment', data),
  sendReminder: (data) => api.post('/email/send-reminder', data),
};

// Placement Acknowledgment (public, no auth) — used only by the legacy ack
// signing page. The "send acknowledgement email" admin action has been
// retired in favour of the Student Placement Confirmation Letter contract flow.
export const placementAckAPI = {
  get: (token) => axios.get(`${API_URL}/api/placement-ack/${token}`),
  sign: (token, data) => axios.post(`${API_URL}/api/placement-ack/${token}/sign`, data),
};

// Email Templates (admin-editable subject + body for transactional emails)
export const emailTemplatesAPI = {
  list: () => api.get('/email-templates'),
  get: (key) => api.get(`/email-templates/${key}`),
  update: (key, data) => api.put(`/email-templates/${key}`, data),
};

// Assessment Rules — per Year+Course mapping of how many assessments a placement gets
export const assessmentRulesAPI = {
  list: () => api.get('/assessment-rules'),
  create: (data) => api.post('/assessment-rules', data),
  update: (id, data) => api.put(`/assessment-rules/${id}`, data),
  delete: (id) => api.delete(`/assessment-rules/${id}`),
  resolveForStudent: (studentId) => api.get(`/assessment-rules/resolve/${studentId}`),
};

// Placement Cycles (Phase 49) — the top-level cohort/term container
export const cyclesAPI = {
  list: () => api.get('/cycles'),
  create: (data) => api.post('/cycles', data),
  get: (id) => api.get(`/cycles/${id}`),
  getPhase: (id, phaseId) => api.get(`/cycles/${id}/phases/${phaseId}`),
  update: (id, data) => api.patch(`/cycles/${id}`, data),
  resyncAssessments: (id) => api.post(`/cycles/${id}/resync-assessments`),
  remove: (id) => api.delete(`/cycles/${id}`),
  excludeStudent: (id, studentId) => api.post(`/cycles/${id}/exclude-student`, { student_id: studentId }),
  includeStudent: (id, studentId) => api.post(`/cycles/${id}/include-student`, { student_id: studentId }),
  addStudent: (id, studentId) => api.post(`/cycles/${id}/add-student`, { student_id: studentId }),
  removeStudent: (id, studentId) => api.post(`/cycles/${id}/remove-student`, { student_id: studentId }),
  addRestaurant: (id, restaurantId) => api.post(`/cycles/${id}/add-restaurant`, { restaurant_id: restaurantId }),
  removeRestaurant: (id, restaurantId) => api.post(`/cycles/${id}/remove-restaurant`, { restaurant_id: restaurantId }),
  duplicate: (id) => api.post(`/cycles/${id}/duplicate`),
  previewImpact: (id, changes) => api.post(`/cycles/${id}/impact`, changes),
  rosterPreview: (cohort) => api.post('/cycles/roster-preview', cohort),
  listOrphans: (id) => api.get(`/cycles/${id}/orphans`),
  resolveOrphan: (id, placementId, body) => api.post(`/cycles/${id}/orphans/${placementId}/resolve`, body),
};

// Documents overview across all placements
export const placementDocsAPI = {
  overview: () => api.get('/placements/documents-overview'),
  exportZip: (data) => api.post('/contracts/export-zip', data, { responseType: 'blob' }),
  // Job-based export (recommended for large cohorts — bypasses Cloudflare timeouts)
  startExport: (data) => api.post('/contracts/export-zip/start', data),
  exportStatus: (jobId) => api.get(`/contracts/export-zip/status/${jobId}`),
  cancelExport: (jobId) => api.post(`/contracts/export-zip/cancel/${jobId}`),
  activeExports: () => api.get('/contracts/export-zip/active'),
  downloadExport: (jobId, onDownloadProgress) => api.get(
    `/contracts/export-zip/download/${jobId}`,
    { responseType: 'blob', onDownloadProgress }
  ),
  previewContract: (contractId) => api.get(`/contracts/${contractId}/preview.pdf`, { responseType: 'blob' }),
};

// Orphan repair (admin-only cleanup for placements whose restaurant was deleted)
export const orphanAPI = {
  listPlacements: () => api.get('/admin/orphans/placements'),
  reassignPlacement: (placementId, restaurantId) =>
    api.post(`/admin/orphans/placements/${placementId}/reassign`, { restaurant_id: restaurantId }),
  deletePlacement: (placementId) => api.delete(`/admin/orphans/placements/${placementId}`),
};

export default api;
